import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { dimOutName } from "@/lib/pivotEngine";
import { TableFormatEditor } from "@/components/bi/TableFormatEditor";
import { Table } from "lucide-react";

function Row({ label, children }) {
  return (
    <div className="flex items-center justify-between gap-2 py-0.5">
      <Label className="text-[11px] text-[color:var(--ink-3)] shrink-0">{label}</Label>
      {children}
    </div>
  );
}

// Data table options (items 74-77): hidden columns, column filters, align/width, wrap.
export function TableEditor({ config, opts, patchOpts }) {
  const t = opts.tableOpts || {};
  const set = (p) => patchOpts({ tableOpts: { ...t, ...p } });
  const dimCols = (config.dimensions || []).filter((d) => d && (d.key || d.field)).map(dimOutName);
  const measCols = (config.measures || []).filter(Boolean).map((m) => m.key || m.alias || `${m.agg || "sum"}_${m.field}`);
  const cols = [...new Set([...dimCols, ...measCols])].filter(Boolean);
  const hidden = new Set(t.hidden || []);
  const st = (c) => t.styles?.[c] || {};
  const setStyle = (c, p) => set({ styles: { ...(t.styles || {}), [c]: { ...st(c), ...p } } });
  const toggleHide = (c, hide) => { const h = new Set(t.hidden || []); if (hide) h.add(c); else h.delete(c); set({ hidden: [...h] }); };

  return (
    <div data-testid="table-editor" className="space-y-2">
      <Label className="text-[11px] text-[color:var(--ink-2)] font-medium flex items-center gap-1"><Table className="w-3 h-3" />Table options</Label>
      <Row label="Header filter menus"><Switch data-testid="table-header-filters" checked={t.headerFilters !== false} onCheckedChange={(v) => set({ headerFilters: v })} /></Row>
      {Object.keys(t.colFilters || {}).length > 0 && (
        <button data-testid="table-clear-saved-filters" onClick={() => set({ colFilters: {} })} className="w-full h-6 rounded text-[10px] text-[color:var(--danger)] hover:bg-[color:var(--surface-2)]">
          Clear saved header filters ({Object.keys(t.colFilters).length})
        </button>
      )}
      {t.defaultSort?.col && (
        <Row label={`Default sort: ${String(t.defaultSort.col).replace(/_/g, " ")} ${t.defaultSort.dir === "desc" ? "↓" : "↑"}`}>
          <button data-testid="table-clear-default-sort" onClick={() => set({ defaultSort: undefined })} className="text-[10px] text-[color:var(--ink-3)] hover:text-[color:var(--danger)]">reset</button>
        </Row>
      )}
      <Row label="Quick filter row"><Switch data-testid="table-filter-row" checked={!!t.filterRow} onCheckedChange={(v) => set({ filterRow: v })} /></Row>
      <Row label="Wrap text"><Switch data-testid="table-wrap" checked={!!t.wrap} onCheckedChange={(v) => set({ wrap: v })} /></Row>
      <Row label="Striped rows"><Switch data-testid="table-striped" checked={!!t.striped} onCheckedChange={(v) => set({ striped: v })} /></Row>
      <Row label="Compact density"><Switch data-testid="table-dense" checked={!!t.dense} onCheckedChange={(v) => set({ dense: v })} /></Row>
      <Row label="Rows per page">
        <Select value={String(t.pageSize || 0)} onValueChange={(v) => set({ pageSize: Number(v) || 0 })}>
          <SelectTrigger data-testid="table-page-size" className="bg-[color:var(--surface-2)] w-[96px] h-6 text-[10px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="0">Off (all)</SelectItem>
            <SelectItem value="10">10</SelectItem>
            <SelectItem value="25">25</SelectItem>
            <SelectItem value="50">50</SelectItem>
            <SelectItem value="100">100</SelectItem>
          </SelectContent>
        </Select>
      </Row>
      {cols.length > 0 && (
        <div className="rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface)] p-2 space-y-1.5">
          <Label className="text-[11px] text-[color:var(--ink-2)] font-medium">Columns (show, align, width)</Label>
          {cols.map((c, i) => (
            <div key={c} className="flex items-center gap-1.5">
              <span className="text-[11px] text-[color:var(--ink-2)] flex-1 min-w-0 truncate" title={c}>{c.replace(/_/g, " ")}</span>
              <Select value={st(c).align || "left"} onValueChange={(v) => setStyle(c, { align: v })}>
                <SelectTrigger data-testid={`table-col-align-${i}`} className="bg-[color:var(--surface-2)] w-[76px] h-6 text-[10px] shrink-0"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="left">Left</SelectItem>
                  <SelectItem value="center">Center</SelectItem>
                  <SelectItem value="right">Right</SelectItem>
                </SelectContent>
              </Select>
              <Input data-testid={`table-col-width-${i}`} type="number" min={40} placeholder="auto" value={st(c).width ?? ""}
                onChange={(e) => setStyle(c, { width: e.target.value ? Number(e.target.value) : "" })}
                title="Width (px)" className="bg-[color:var(--surface-2)] w-16 h-6 text-[10px] shrink-0" />
              <Switch data-testid={`table-col-hide-${i}`} checked={!hidden.has(c)} onCheckedChange={(v) => toggleHide(c, !v)} title="Show / hide column" />
            </div>
          ))}
          <p className="text-[10px] text-[color:var(--ink-3)]">Toggle shows/hides a column. Width in px (blank = auto).</p>
        </div>
      )}
      <TableFormatEditor opts={opts} patchOpts={patchOpts} cols={cols} />
    </div>
  );
}
